import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ChatSource } from '../../../models/chat.model';
import { ChatItem } from '../../../models/chat-ui.model';
import { DocumentService } from '../../document.service';
import { FrontendLoggerService } from '../../frontend-logger.service';

/** Narrow shape of the per-source metadata that carries the backend document id. */
export interface ChatSourceDocumentMetadata {
  document_id?: string | null;
  original_filename?: string | null;
}

/** Deterministic, code-derived reference to the answer's source document. */
export interface AnswerSourceDocument {
  documentId: string;
  filename: string | null;
}

/** Fecha detectada dentro del resumen de una fuente. */
export interface SourceSummaryDate {
  date: string;
  context?: string;
}

/** Resumen de una fuente, parseado de forma amigable desde el `summary` (JSON o texto). */
export interface ParsedSourceSummary {
  category?: string;
  subcategory?: string;
  keywords: string[];
  detectedDates: SourceSummaryDate[];
  /** Texto plano cuando el resumen no es un JSON estructurado. */
  text?: string;
}

/**
 * Logica de fuentes de la respuesta extraida de `ChatComponent` para mantener el
 * componente como coordinador delgado. Deriva (de forma determinista, a partir de los
 * datos del mensaje) los documentos descargables y el resumen amigable de una fuente,
 * y orquesta la obtencion de la URL firmada de descarga (sin construir la URL).
 */
@Injectable({
  providedIn: 'root'
})
export class ChatAnswerSourcesService {
  constructor(
    private documentService: DocumentService,
    private frontendLogger: FrontendLoggerService
  ) {}

  /**
   * Deriva, de forma determinista y SOLO a partir de los datos del mensaje,
   * los documentos fuente a los que se ancla la respuesta del bot.
   * Recorre `message.sources`, toma las que tengan `metadata.document_id` no vacio,
   * y deduplica por `document_id` (preservando el `original_filename`).
   * El orden preserva la aparicion; las fuentes marcadas como `selected` van primero.
   * Devuelve [] si no hay ningun `document_id` disponible (no se renderiza nada).
   */
  getAnswerSourceDocuments(message: ChatItem | null | undefined): AnswerSourceDocument[] {
    if (!message || message.type !== 'system') {
      return [];
    }
    // Recarga: los documentos descargables vienen ya resueltos y desacoplados de
    // `sources` (para no afectar el panel de detalles).
    const preresolved = message.downloadDocuments;
    if (Array.isArray(preresolved) && preresolved.length > 0) {
      const seenDocs = new Set<string>();
      const docs: AnswerSourceDocument[] = [];
      for (const entry of preresolved) {
        const documentId = entry.documentId ? String(entry.documentId).trim() : '';
        if (!documentId || seenDocs.has(documentId)) {
          continue;
        }
        seenDocs.add(documentId);
        const filename = (entry.filename || '').toString().trim();
        docs.push({ documentId, filename: filename || null });
      }
      return docs;
    }
    // Query fresca: se derivan de las fuentes (relevant_chunks con document_id).
    const sources = message.sources;
    if (!Array.isArray(sources) || sources.length === 0) {
      return [];
    }

    const withDocId = sources.filter(source => !!this.readSourceMetadata(source).document_id);
    if (withDocId.length === 0) {
      return [];
    }

    // Las fuentes seleccionadas primero, manteniendo el orden relativo original.
    const ordered = [
      ...withDocId.filter(source => source.selected === true),
      ...withDocId.filter(source => source.selected !== true)
    ];

    const seen = new Set<string>();
    const documents: AnswerSourceDocument[] = [];
    for (const source of ordered) {
      const doc = this.toAnswerSourceDocument(source);
      if (!doc || seen.has(doc.documentId)) {
        continue;
      }
      seen.add(doc.documentId);
      documents.push(doc);
    }
    return documents;
  }

  /** Lee el metadata de la fuente con tipado estrecho (sin `any`). */
  readSourceMetadata(source: ChatSource): ChatSourceDocumentMetadata {
    const metadata = source.metadata as ChatSourceDocumentMetadata | undefined | null;
    return metadata ?? {};
  }

  toAnswerSourceDocument(source: ChatSource): AnswerSourceDocument | null {
    const metadata = this.readSourceMetadata(source);
    const documentId = metadata.document_id ? String(metadata.document_id) : '';
    if (!documentId.trim()) {
      return null;
    }
    const filename = (metadata.original_filename || source.original_filename || '').toString().trim();
    return { documentId, filename: filename || null };
  }

  /** Convierte el `title` (summary) de una fuente en un resumen estructurado o texto. */
  parseSourceSummary(source: ChatSource | null | undefined): ParsedSourceSummary | null {
    const raw = (source?.title ?? '').toString().trim();
    if (!raw) {
      return null;
    }
    let data: unknown;
    try {
      data = JSON.parse(raw);
    } catch {
      // No es JSON: mostrar como texto plano.
      return { keywords: [], detectedDates: [], text: raw };
    }
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return { keywords: [], detectedDates: [], text: raw };
    }
    const obj = data as Record<string, unknown>;
    const keywords = Array.isArray(obj['keywords'])
      ? (obj['keywords'] as unknown[]).map(k => String(k).trim()).filter(Boolean)
      : [];
    const detectedDates = (Array.isArray(obj['detected_dates']) ? (obj['detected_dates'] as unknown[]) : [])
      .map(entry => {
        const item = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {};
        const date = item['date'] ? String(item['date']).trim() : '';
        const context = item['context'] ? String(item['context']).trim() : undefined;
        return { date, context };
      })
      .filter(d => !!d.date);
    const category = obj['category'] ? String(obj['category']).trim() : undefined;
    const subcategory = obj['subcategory'] ? String(obj['subcategory']).trim() : undefined;
    // Si no hay ningun campo reconocido, degradar a texto crudo.
    if (!category && !subcategory && keywords.length === 0 && detectedDates.length === 0) {
      return { keywords: [], detectedDates: [], text: raw };
    }
    return { category, subcategory, keywords, detectedDates };
  }

  /** Return a normalized download URL or null if none. Accept relative or absolute paths. */
  getSourceDownloadUrl(source: ChatSource | null): string | null {
    try {
      if (!source) {
        return null;
      }
      const loc = source.location || source.metadata?.source || '';
      if (!loc) {
        return null;
      }
      // If the location is already an absolute URL, return it.
      if (/^https?:\/\//i.test(loc)) {
        return loc;
      }
      // Otherwise, treat it as an app-relative path and return as-is so the browser will resolve it.
      return loc;
    } catch {
      return null;
    }
  }

  /**
   * Descarga un documento fuente puntual de la respuesta. Obtiene la URL firmada desde
   * el backend (DocumentService) y la abre en una pestana nueva. Nunca construye la URL.
   * El `destroy$` del componente controla el ciclo de vida de la suscripcion.
   */
  downloadAnswerDocument(documentId: string, destroy$: Observable<unknown>, event?: MouseEvent): void {
    try {
      event?.preventDefault();
      event?.stopPropagation();
    } catch { }

    const safeId = typeof documentId === 'string' ? documentId.trim() : '';
    if (!safeId) {
      this.frontendLogger.warn('No hay documento fuente descargable para la respuesta', 'ChatAnswerSourcesService.downloadAnswerDocument');
      return;
    }

    this.documentService.getDocumentPublicUrl(safeId)
      .pipe(take(1), takeUntil(destroy$))
      .subscribe({
        next: (url: string) => {
          const safeUrl = typeof url === 'string' ? url.trim() : '';
          if (!safeUrl) {
            this.frontendLogger.warn('publicUrl vacia para documento ' + safeId, 'ChatAnswerSourcesService.downloadAnswerDocument');
            return;
          }
          try {
            window.open(safeUrl, '_blank', 'noopener');
          } catch (err: unknown) {
            this.frontendLogger.error('No se pudo abrir la URL de descarga: ' + this.formatError(err), 'ChatAnswerSourcesService.downloadAnswerDocument');
          }
        },
        error: (err: unknown) => {
          this.frontendLogger.error('Error al obtener la URL de descarga del documento: ' + this.formatError(err), 'ChatAnswerSourcesService.downloadAnswerDocument');
        }
      });
  }

  private formatError(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message?: unknown }).message || error);
    }

    return String(error);
  }
}
